import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useCallback, useEffect, useState, useRef } from 'react';
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Menu, MenuItem, Select, SelectChangeEvent, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTranslation } from 'react-i18next';
import { menuItems } from './menu-items';
import React from 'react';
import { Language, handleSelectedLanguageChange } from '../../store/app-slice/app';
import i18next from 'i18next';
import signalRService from '../../signalr/config';
import { red } from '@mui/material/colors';
import SubscriptionDetails from './SubscriptionDetails';
import { authHeader, CurrentSubscriptionDetails, updateCurrentSubscription } from '../../store/auth-slice/auth-slice';
import { axiosInstance } from '../../axios/axiosHandler';
import { EndPoints } from '../../axios/endPoints';
import LogoSVG from './utils/logo.png';
import { fetchUserProfileInfo, updatePreferredLanguageCode } from '../../store/profile-slice/profile-slice';
import { fetchSubscriptionPlans, selectHasAnyActiveSubscription } from '../../store/subscription-slice/subscription-slice';
import { PlansSection } from '../profile/subscriptions/PlansSection';
import { updatePreferredLanguage } from '../../services/userProfileService';

function LanguageSelection() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedLanguage, languages } = useAppSelector(
    (state: RootState) => state.app
  );

  function handleChange(e: SelectChangeEvent) {
    const language = languages.find(
      (el: Language) => el.id === parseInt(e?.target?.value)
    );
    if (language) {
      dispatch(handleSelectedLanguageChange(language));
      dispatch(updatePreferredLanguageCode(language.code));
      i18next.changeLanguage(language.code);
      void updatePreferredLanguage(language.code);
    }
  }

  return (
    <FormControl sx={{ m: 1, width: '100%' }} size="small">
      <InputLabel id="demo-select-small-label"></InputLabel>
      <Select
        labelId="demo-select-small-label"
        id="demo-select-small"
        value={selectedLanguage}
        label=""
        onChange={handleChange}
      >
        {languages.length === 0 ? (
          <MenuItem value="">
            <em>{t('NO_AVAILABLE_LANGUAGES_TEXT')}</em>
          </MenuItem>
        ) : (
          languages.map((el: Language) => (
            <MenuItem key={el.id} value={el.id}>
              {el.name}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}

const DrawerHeader = styled('div')(({ theme }) => ({
  minHeight: window.innerWidth <= 768 ? 64 : 128,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: 'white',
}));

export default function MainLayout(props: any) {
  const { user, currentSubscription } = useAppSelector((state: RootState) => state.auth);
  const userInfo = useAppSelector((state: RootState) => state.profile.userInfo);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentSubscriptionFE, setCurrentSubscriptionFE] = useState<CurrentSubscriptionDetails | null>(null);
  const [hasSubscriptionCheckCompleted, setHasSubscriptionCheckCompleted] = useState(false);
  const [showSubscriptionRequiredDialog, setShowSubscriptionRequiredDialog] = useState(false);
  const openProfile = Boolean(anchorEl);
  const prevIsMobileRef = useRef(false);
  const hasAnyActiveSubscription = useAppSelector(selectHasAnyActiveSubscription);
  const isSubscriptionExemptUser = userInfo?.isSubscriptionExempt === true;

  const fetchCurrentSubscription = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        EndPoints.GET_CURRENT_SUBSCRIPTION,
        authHeader()
      );

      setCurrentSubscriptionFE(response.data);
    } catch (error) {
      console.error('Error fetching current subscription', error);
      setCurrentSubscriptionFE(null);
    }
  }, []);

  const refreshSubscriptionStateFromServer = useCallback(async () => {
    const profileAction = await dispatch(fetchUserProfileInfo());
    if (fetchUserProfileInfo.fulfilled.match(profileAction)) {
      dispatch(updateCurrentSubscription(profileAction.payload?.currentSubscription ?? null));
    } else {
      dispatch(updateCurrentSubscription(null));
    }

    await fetchCurrentSubscription();
  }, [dispatch, fetchCurrentSubscription]);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    fetchCurrentSubscription();
    setAnchorEl(event.currentTarget);
  };
  const handleProfileClose = () => {
    setAnchorEl(null);
  };
  const handleDrawerToggle = () => {
    setOpen((value) => !value);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };
  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);
  const isMobile = width <= 1215;

  useEffect(() => {
    if (!prevIsMobileRef.current && isMobile) {
      setOpen(false);
    }
    prevIsMobileRef.current = isMobile;
  }, [isMobile]);

  const menus = menuItems(t, dispatch, navigate);
  const hasAccessibleSubscription = isSubscriptionExemptUser || currentSubscription?.isActiveSubscription;
  const hasSubscriptionAccess = (item: (typeof menus)[number]) => !item.checkActiveSubscription || hasAccessibleSubscription;
  const profileMenuKeys = ['help', 'logout', 'colorPicker', 'userProfile', "support"];
  const mainMenuRightKeys = ['tierManagement', 'userManagement', 'userProfile'];
  const mainMenuLeftKeys = ['mlCreator', 'mlTester', 'prediction', 'optimization'];

  const mainMenuItemsLeft = menus.filter(item => mainMenuLeftKeys.includes(item.key));
  const mainMenuItemsRight = menus.filter(item => mainMenuRightKeys.includes(item.key));
  const mobileMenuItems = [...mainMenuItemsLeft, ...mainMenuItemsRight];
  
  const profileMenuItems = menus.filter(item => profileMenuKeys.includes(item.key));

  const { languages, selectedLanguageCode } = useAppSelector((state: RootState) => state.app);

  useEffect(() => {
    const preferredCode = userInfo?.preferredLanguageCode;
    if (!preferredCode || !languages.length) return;
    if (preferredCode === selectedLanguageCode) return;

    const language = languages.find((l: Language) => l.code === preferredCode);
    if (language) {
      dispatch(handleSelectedLanguageChange(language));
      i18next.changeLanguage(language.code);
    }
  }, [userInfo?.preferredLanguageCode, languages, selectedLanguageCode, dispatch]);

  useEffect(() => {
    const startConnection = async () => {
      const jsonString = localStorage.getItem("auth");
      if (!jsonString) return;

      let token: string | null = null;
      try {
        token = JSON.parse(jsonString)?.token ?? null;
      } catch {
        return;
      }

      if (!token) return;
      await signalRService.startConnection(dispatch, token);
    };
    void startConnection();
    return () => {
      signalRService.stopConnection();
    };
  }, [dispatch]);

  useEffect(() => {
    const initializeSubscriptionState = async () => {
      await refreshSubscriptionStateFromServer();
      setHasSubscriptionCheckCompleted(true);
    };

    void initializeSubscriptionState();
  }, [refreshSubscriptionStateFromServer]);

  useEffect(() => {
    if (!hasSubscriptionCheckCompleted) {
      return;
    }

    if (!hasAnyActiveSubscription && !isSubscriptionExemptUser) {
      setShowSubscriptionRequiredDialog(true);
      void dispatch(fetchSubscriptionPlans({ showLoader: true }));
    }
  }, [dispatch, hasAnyActiveSubscription, hasSubscriptionCheckCompleted, isSubscriptionExemptUser]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ background: 'var(--card-header-background-color)' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Toolbar>
            {isMobile && (
              <IconButton
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                edge="start"
                sx={{ marginRight: 5 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={LogoSVG}
                alt="MedPRO logo"
                sx={{
                  height: 48,
                  width: 'auto',
                  filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))',
                }}
              />
              {process.env.REACT_APP_BUILD_ENV === "TEST" ? (
                <Box component="span" sx={{ color: red[500], fontWeight: 'bold' }}>
                  TEST
                </Box>
              ) : null}
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
              <Tooltip title="Account">
                <IconButton
                  onClick={handleProfileClick}
                  size="small"
                  sx={{ ml: 2 }}
                  aria-controls={openProfile ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openProfile ? 'true' : undefined}
                >
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {user ? user.email?.charAt(0)?.toUpperCase() : 'A'}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={openProfile}
              onClose={handleProfileClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem>
                <AccountCircleIcon color="primary"></AccountCircleIcon>
                <Typography ml={1} sx={{ typography: { sm: 'subtitle1', xs: 'body2' } }}>
                  {user.email} ({user.role})
                </Typography>
              </MenuItem>
              <SubscriptionDetails subscription={currentSubscriptionFE} />
              <Divider />
              <MenuItem>
                <LanguageSelection />
              </MenuItem>
              {profileMenuItems.map(item => (
                <MenuItem key={item.key} onClick={item.onClickFunction}>
                  <Typography ml={1}>{item.title}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Toolbar>
           {!isMobile && (
          <Toolbar
            sx={{
              background: 'white !important',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {/* LEFT MENU */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {mainMenuItemsLeft
                .filter((el) => !el.roles || el.roles.length === 0 || el.roles.includes(user.role))
                .filter(hasSubscriptionAccess)
                .map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: item.hidden ? 'none' : 'block',
                      background: 'var(--button-background-color)',
                      borderRadius: '10px',
                      ...(window.location.pathname === item.route
                        ? {
                            boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
                            transform: 'translateY(-2px)',
                          }
                        : {
                            opacity: 0.8,
                          }),
                    }}
                  >
                    <Button
                      onClick={item.onClickFunction}
                      startIcon={item.icon}
                      sx={{
                        color: 'var(--button-text-color)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      {item.title}
                    </Button>
                  </Box>
                ))}
            </Box>

            {/* RIGHT MENU */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {mainMenuItemsRight
                .filter((el) => !el.roles || el.roles.length === 0 || el.roles.includes(user.role))
                .filter(hasSubscriptionAccess)
                .map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: item.hidden ? 'none' : 'block',
                      background: 'var(--button-background-color)',
                      borderRadius: '10px',
                      ...(window.location.pathname === item.route
                        ? {
                            boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
                            transform: 'translateY(-2px)',
                          }
                        : {
                            opacity: 0.8,
                          }),
                    }}
                  >
                    <Button
                      onClick={item.onClickFunction}
                      startIcon={item.icon}
                      sx={{
                        color: 'var(--button-text-color)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      {item.title}
                    </Button>
                  </Box>
                ))}
            </Box>
          </Toolbar>
        )}
        </Box>
      </AppBar>
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={handleDrawerClose}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
            },
          }}
        >
          <DrawerHeader />
          <List sx={{ pt: 0, pb: 0 }}>
            {mobileMenuItems
              .filter((el) => !el.roles || el.roles.length === 0 || el.roles.includes(user.role))
              .filter(hasSubscriptionAccess)
              .map((item, index) => (
                <ListItem
                  sx={{
                    display: item.hidden ? 'none' : 'block',
                  }}
                  key={index}
                  disablePadding
                >
                  <ListItemButton
                    selected={window.location.pathname === item.route}
                    onClick={() => {
                      if (item.onClickFunction) {
                        setTimeout(() => {
                          try {
                            item.onClickFunction();
                          } catch (error) {
                            console.error('Error in onClickFunction:', error);
                          }
                        }, 0);
                      }
                      handleDrawerClose();
                    }}
                    sx={{
                      ...(window.location.pathname === item.route && {
                        backgroundColor: 'var(--button-background-color) !important',
                        color: 'var(--button-text-color) !important',
                        opacity: 0.8
                      }),
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.title} />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </MuiDrawer>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          p: 3
        }}
      >
        <DrawerHeader />
        <Box sx={{ flexGrow: 1, pb: 3, minWidth: 0 }}>
          {props.children}
        </Box>
      </Box>

      <Dialog
        open={showSubscriptionRequiredDialog}
        onClose={() => setShowSubscriptionRequiredDialog(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            borderTop: '4px solid var(--card-header-background-color)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('PROFILE_SUBSCRIPTION_REQUIRED_TITLE')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: 'var(--link-text-color)' }}>
            {t('PROFILE_SUBSCRIPTION_REQUIRED_BODY')}
          </Typography>
          <PlansSection hidePortalCard compactTopPadding />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button className='button-color' onClick={() => setShowSubscriptionRequiredDialog(false)} variant="contained">
            {t('CLOSE_BUTTON')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
